/** Messaging Drawer — floating chat panel accessible from any page.
 *
 *  Two-pane layout: thread list on the left, active thread messages on
 *  the right. Real-time via /api/ws (see hooks/useWebSocket). Meeting
 *  invites pushed on the org channel also surface here as a toast so
 *  committee members are notified even without an active thread.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Users, Megaphone, Video, Mail, User as UserIcon, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useMailbox } from "@/contexts/MailboxContext";
import { useWebSocket } from "@/hooks/useWebSocket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

const KIND_ICON = {
  dm:             Users,
  project:        Users,
  org_broadcast:  Megaphone,
  org_mailbox:    Mail,
  cubicle:        Users,
};

export default function ChatDrawer() {
  const { user } = useAuth();
  const { activeContext, contexts } = useMailbox();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [totalUnread, setTotalUnread] = useState(0);
  const bottomRef = useRef(null);

  const active = useMemo(() => threads.find((t) => t.id === activeId), [threads, activeId]);

  const loadThreads = async () => {
    try {
      const params = activeContext?.id && activeContext.id !== "self"
        ? { context: activeContext.id }
        : { context: "self" };
      const { data } = await api.get("/messaging/threads", { params });
      setThreads(data.items || []);
      setTotalUnread((data.items || []).reduce((n, t) => n + (t.unread || 0), 0));
    } catch { /* not logged in / permission */ }
  };

  const loadMessages = async (tid) => {
    try {
      const { data } = await api.get(`/messaging/threads/${tid}/messages`, { params: { limit: 200 } });
      setMessages(data.messages || []);
      await api.post(`/messaging/threads/${tid}/read`).catch(() => {});
    } catch (e) { console.warn("[chat load]", e?.message); }
  };

  useEffect(() => { if (user) void loadThreads(); }, [user?.id, activeContext?.id]);
  useEffect(() => { if (activeId) void loadMessages(activeId); }, [activeId]);
  // Reset selection when context changes to avoid cross-context leaks.
  useEffect(() => { setActiveId(null); }, [activeContext?.id]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
  }, [messages.length]);

  // Live push handling
  useWebSocket((evt) => {
    if (!evt || !evt.type) return;
    if (evt.type === "message.new") {
      if (evt.thread_id === activeId) {
        setMessages((prev) => [...prev, evt.message]);
        api.post(`/messaging/threads/${activeId}/read`).catch(() => {});
      } else {
        setTotalUnread((n) => n + 1);
        void loadThreads();
      }
    } else if (evt.type === "meeting.broadcast") {
      toast(`New meeting invite: ${evt.room?.title || "Untitled"}`, {
        description: "Check the Meeting Rooms panel to Accept / Reject.",
        icon: "📅",
      });
    } else if (evt.type === "meeting.live") {
      toast.success(`${evt.title} is LIVE now — jump into the video room.`, {
        icon: "🎥",
      });
    } else if (evt.type === "meeting.status_changed") {
      // silent refresh — dashboard panels react on their own polling.
    }
  });

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeId) return;
    setDraft("");
    try {
      await api.post(`/messaging/threads/${activeId}/messages`, { body });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  if (!user) return null;

  return (
    <>
      {/* FAB */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-white text-black shadow-lg hover:bg-white/90 flex items-center justify-center"
        title="Messaging"
        data-testid="chat-drawer-fab"
      >
        <MessageCircle size={20}/>
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full bg-red-500 text-white text-[10px] px-1.5 flex items-center justify-center"
                data-testid="chat-unread-badge">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {/* Slide-in drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="chat-drawer">
          <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)}/>
          <div className="w-full max-w-3xl h-full bg-neutral-950 border-l border-white/10 flex">
            {/* Thread list */}
            <div className="w-72 border-r border-white/10 flex flex-col">
              <div className="p-4 flex items-center justify-between border-b border-white/10">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/45">Messages</div>
                  <div className="font-display text-lg" data-testid="chat-drawer-context-title">
                    {activeContext?.kind === "personal" ? "Personal Inbox" : `${activeContext?.title || "Page"} · Mailbox`}
                  </div>
                </div>
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/60">
                  <X size={16}/>
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto" data-testid="chat-thread-list">
                {threads.length === 0 ? (
                  <div className="text-xs text-white/45 p-6 text-center">
                    No conversations yet. Start a DM from anyone&apos;s profile card.
                  </div>
                ) : threads.map((t) => {
                  const Icon = KIND_ICON[t.kind] || Users;
                  const isActive = t.id === activeId;
                  return (
                    <button key={t.id}
                            type="button"
                            onClick={() => setActiveId(t.id)}
                            className={"w-full text-left px-4 py-3 border-b border-white/5 flex items-center gap-3 hover:bg-white/[0.04] " +
                                       (isActive ? "bg-white/[0.06]" : "")}
                            data-testid={`chat-thread-${t.id}`}>
                      <Icon size={16} className="shrink-0 text-white/55"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{t.title || "(untitled)"}</div>
                        <div className="text-[11px] text-white/45 truncate">
                          {t.last_message?.body || "No messages yet"}
                        </div>
                      </div>
                      {t.unread > 0 && (
                        <span className="h-4 min-w-4 px-1.5 rounded-full bg-emerald-500 text-black text-[10px] font-bold flex items-center justify-center">
                          {t.unread}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Message pane */}
            <div className="flex-1 flex flex-col">
              {!active ? (
                <div className="flex-1 flex items-center justify-center text-white/45 text-sm">
                  Select a conversation to start reading
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-white/10">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
                      {active.kind.replace("_", " ")}
                    </div>
                    <div className="font-display text-xl">{active.title}</div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                    {messages.map((m) => {
                      const mine = m.sender_id === user.id;
                      return (
                        <div key={m.id} className={"flex " + (mine ? "justify-end" : "justify-start")}
                             data-testid={`chat-msg-${m.id}`}>
                          <div className={"max-w-[70%] rounded-xl px-3 py-2 " +
                                          (mine ? "bg-white text-black" : "bg-white/10 text-white")}>
                            {!mine && (
                              <div className="text-[10px] tracking-[0.18em] uppercase text-white/55 mb-0.5">
                                {m.sender_name}
                              </div>
                            )}
                            <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                            <div className={"text-[10px] mt-0.5 " + (mine ? "text-black/45" : "text-white/40")}>
                              {fmtTime(m.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef}/>
                  </div>
                  <div className="p-3 border-t border-white/10 flex gap-2 items-center">
                    <Input value={draft}
                           onChange={(e) => setDraft(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                           placeholder="Write a message…"
                           className="flex-1 bg-black/40 border-white/15"
                           data-testid="chat-input"/>
                    <Button onClick={send}
                            className="bg-white text-black hover:bg-white/90"
                            data-testid="chat-send-btn">
                      <Send size={14}/>
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
